import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DEFAULT_PAGE_SIZE,
  Banner,
  Card,
  Chip,
  DataTable,
  FilterChip,
  Kpi,
  PageHead,
  Pager,
  RoleChip,
  SearchInput,
  Segmented,
  StateBlock,
  type Column,
} from '@w7/ui'
import { AsOf, AccountLink } from '@/app/bits'
import { count, EMPTY, lll, percent } from '@/lib/format'
import { hasLivePlayerMfa } from '@/api/client'
import { isDemoSession } from '@/auth/oidc'
import { useDownline } from '@/app/queries'
import { ROLE_LABEL, ROLE_SHORT, isAgent, type Account, type Role } from '@/data/types'
import { usePlayerMfaSummary, usePlayerMfaUsers } from './hooks'
import type { PlayerMfaUser } from './types'
import { demoAgentSummary } from './agentDemo'
import { DEMO_PLAYER_MFA_USERS } from './demo'
import { useAgentMfaBatch } from './agentHooks'
import type { AgentMfaStatus, SignIn2faAudience } from './agentTypes'
import './sign2fa.css'

/**
 * Sign-in 2FA — combined adoption for:
 *  · Members → player portal (Player-API: authenticator / SMS)
 *  · Agents → Backend-UI console (Keycloak: authenticator / passkey)
 *
 * Table rows are always hierarchy accounts so a click opens `/member/:id`.
 */

const PAGE_SIZE = DEFAULT_PAGE_SIZE

const AGENT_ROLES: Role[] = [
  'company-master',
  'triple-master',
  'double-master',
  'super-master',
  'master',
]

type MfaFilter = 'all' | 'on' | 'off'
type PlayerMethodFilter = 'any' | 'totp' | 'sms'

type MemberRow = PlayerMfaUser & { memberId: string }

type AgentRow = {
  id: string
  username: string
  displayName: string
  role: Role
  mfa: AgentMfaStatus | null
}

function demoMfaForIndex(i: number): PlayerMfaUser {
  return DEMO_PLAYER_MFA_USERS[i % DEMO_PLAYER_MFA_USERS.length]!
}

function memberRowFromAccount(
  account: Account,
  mfa: PlayerMfaUser | null,
  demoIndex: number,
): MemberRow {
  const base = mfa ?? demoMfaForIndex(demoIndex)
  return {
    ...base,
    id: account.id,
    memberId: account.id,
    username: account.username,
    displayName: account.displayName || account.username,
    email: mfa?.email || base.email,
  }
}

function MembersPanel() {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [mfa, setMfa] = useState<MfaFilter>('all')
  const [method, setMethod] = useState<PlayerMethodFilter>('any')
  const [page, setPage] = useState(1)

  const demoish = isDemoSession() || !hasLivePlayerMfa()
  const summaryQuery = usePlayerMfaSummary()
  // Full MFA catalog for username join (hierarchy id ≠ Player-API id).
  const mfaCatalog = usePlayerMfaUsers({
    limit: 2000,
    offset: 0,
    mfa: 'all',
    method: 'any',
  })

  const downlineQuery = useDownline({
    roles: ['player'],
    search: q || undefined,
    page: 1,
    pageSize: 500,
  })

  const mfaByUsername = useMemo(() => {
    const map = new Map<string, PlayerMfaUser>()
    for (const u of mfaCatalog.data?.users ?? []) {
      map.set(u.username.toLowerCase(), u)
    }
    return map
  }, [mfaCatalog.data])

  const allRows = useMemo(() => {
    const accounts = (downlineQuery.data?.rows ?? []).filter((a) => a.role === 'player')
    let list = accounts.map((account, i) =>
      memberRowFromAccount(
        account,
        demoish ? null : (mfaByUsername.get(account.username.toLowerCase()) ?? null),
        i,
      ),
    )
    if (mfa === 'on') list = list.filter((u) => u.mfaEnabled)
    if (mfa === 'off') list = list.filter((u) => !u.mfaEnabled)
    if (method === 'totp') list = list.filter((u) => u.methods.includes('totp'))
    if (method === 'sms') list = list.filter((u) => u.methods.includes('sms'))
    return list
  }, [downlineQuery.data, mfaByUsername, demoish, mfa, method])

  const total = allRows.length
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const safePage = Math.min(page, pageCount)
  const rows = useMemo(() => {
    const from = (safePage - 1) * PAGE_SIZE
    return allRows.slice(from, from + PAGE_SIZE)
  }, [allRows, safePage])

  const summary = summaryQuery.data
  const asOf =
    downlineQuery.dataUpdatedAt || mfaCatalog.dataUpdatedAt || summaryQuery.dataUpdatedAt || undefined

  const columns = useMemo<Column<MemberRow>[]>(
    () => [
      {
        id: 'user',
        header: 'Member',
        width: 200,
        cell: (row) => (
          <div style={{ minWidth: 0 }}>
            <div className="w7-ellipsis" style={{ fontWeight: 600 }}>
              <AccountLink id={row.memberId} username={row.username} />
            </div>
            <div className="w7-card-sub w7-ellipsis">{row.displayName || row.email}</div>
          </div>
        ),
        sortValue: (row) => row.username,
      },
      {
        id: 'status',
        header: '2FA',
        width: 88,
        cell: (row) => (
          <Chip size="sm" tone={row.mfaEnabled ? 'profit' : 'muted'}>
            {row.mfaEnabled ? 'On' : 'Off'}
          </Chip>
        ),
        sortValue: (row) => (row.mfaEnabled ? 1 : 0),
      },
      {
        id: 'methods',
        header: 'Methods',
        width: 168,
        cell: (row) => (
          <div className="w7-row w7-row-wrap" style={{ gap: 4 }}>
            {row.methods.length === 0 ? (
              <span className="w7-card-sub">{EMPTY}</span>
            ) : (
              row.methods.map((m) => (
                <Chip key={m} size="sm" tone={m === 'totp' ? 'brand' : 'info'}>
                  {m === 'totp' ? 'App' : 'SMS'}
                </Chip>
              ))
            )}
          </div>
        ),
      },
      {
        id: 'phone',
        header: 'SMS',
        width: 128,
        hideBelow: 900,
        cell: (row) => {
          const sms = row.factors.find((f) => f.type === 'sms' && f.enabled)
          return <span className="w7-mono">{sms?.phoneMasked || EMPTY}</span>
        },
      },
      {
        id: 'recovery',
        header: 'Recovery',
        width: 96,
        hideBelow: 1100,
        cell: (row) =>
          row.hasRecoveryCodes ? (
            <Chip size="sm" tone="info">
              Yes
            </Chip>
          ) : (
            <span className="w7-card-sub">{EMPTY}</span>
          ),
        sortValue: (row) => (row.hasRecoveryCodes ? 1 : 0),
      },
      {
        id: 'updated',
        header: 'Updated',
        width: 148,
        hideBelow: 720,
        cell: (row) => <span className="w7-mono">{lll(row.updatedAt)}</span>,
        sortValue: (row) => row.updatedAt,
      },
    ],
    [],
  )

  const goMember = (id: string) => {
    navigate(`/member/${encodeURIComponent(id)}`)
  }

  return (
    <>
      {demoish ? (
        <div className="w7-sign2fa-banner">
          <Banner tone="info" title="Sample member MFA on your downline">
            Rows are real members from your tree — click any row to open their page. MFA flags are
            sample until Player-API is wired.
          </Banner>
        </div>
      ) : null}

      <div className="w7-sign2fa-kpis">
        <Kpi
          size="sm"
          label="Members"
          value={summary ? count(summary.totalUsers) : count(total)}
          meta="In downline scope"
        />
        <Kpi
          size="sm"
          label="2FA on"
          value={summary ? count(summary.mfaEnabled) : count(allRows.filter((r) => r.mfaEnabled).length)}
          tone="profit"
          meta={
            summary
              ? `${percent(summary.adoptionRate)} · ${count(summary.mfaDisabled)} off`
              : undefined
          }
          accent
        />
        <Kpi
          size="sm"
          label="App"
          value={summary ? count(summary.totpEnabled) : '—'}
          tone="brand"
          meta={summary ? `${count(summary.bothEnabled)} also SMS` : undefined}
        />
        <Kpi
          size="sm"
          label="SMS"
          value={summary ? count(summary.smsEnabled) : '—'}
          tone="info"
          meta={summary ? `${count(summary.withRecoveryCodes)} recovery` : undefined}
        />
      </div>

      <div className="w7-sign2fa-toolbar">
        <SearchInput
          label="Search members"
          placeholder="Username or name"
          value={q}
          width={220}
          onChange={(next) => {
            setQ(next)
            setPage(1)
          }}
        />
        <div className="w7-row w7-row-wrap" style={{ gap: 4 }} role="group" aria-label="2FA status">
          {(
            [
              ['all', 'All'],
              ['on', 'On'],
              ['off', 'Off'],
            ] as const
          ).map(([id, label]) => (
            <FilterChip
              key={id}
              active={mfa === id}
              onClick={() => {
                setMfa(id)
                setPage(1)
              }}
            >
              {label}
            </FilterChip>
          ))}
        </div>
        <div className="w7-row w7-row-wrap" style={{ gap: 4 }} role="group" aria-label="Method">
          {(
            [
              ['any', 'Any'],
              ['totp', 'App'],
              ['sms', 'SMS'],
            ] as const
          ).map(([id, label]) => (
            <FilterChip
              key={id}
              active={method === id}
              onClick={() => {
                setMethod(id)
                setPage(1)
              }}
            >
              {label}
            </FilterChip>
          ))}
        </div>
        <span style={{ marginLeft: 'auto' }}>
          <AsOf at={asOf} polls={downlineQuery.isFetching || mfaCatalog.isFetching} />
        </span>
      </div>

      {downlineQuery.isError ? (
        <StateBlock icon="error" title="Could not load members" tone="error">
          {(downlineQuery.error as Error)?.message || 'Hierarchy request failed.'}
        </StateBlock>
      ) : downlineQuery.isLoading && !downlineQuery.data ? (
        <StateBlock icon="refresh" title="Loading…">
          Fetching members in your downline.
        </StateBlock>
      ) : (
        <Card
          flush
          title="Members"
          subtitle={`${count(total)} matching this filter · click a row to open`}
          footer={
            <Pager
              page={safePage}
              pageCount={pageCount}
              onChange={setPage}
              total={total}
              pageSize={PAGE_SIZE}
            />
          }
        >
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(row) => row.memberId}
            label={`${count(total)} members with portal MFA status`}
            density="compact"
            loading={downlineQuery.isFetching && !downlineQuery.data}
            skeletonRows={8}
            onRowClick={(row) => goMember(row.memberId)}
            empty={
              <StateBlock icon="search" title="No members match">
                Clear search or widen the filters.
              </StateBlock>
            }
          />
        </Card>
      )}
    </>
  )
}

function AgentsPanel() {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [mfa, setMfa] = useState<MfaFilter>('all')
  const [page, setPage] = useState(1)
  const demo = isDemoSession()

  const downlineQuery = useDownline({
    roles: AGENT_ROLES,
    search: q || undefined,
    page: 1,
    pageSize: 500,
  })

  const liveAccounts = useMemo(
    () => (downlineQuery.data?.rows ?? []).filter((a) => isAgent(a.role)),
    [downlineQuery.data],
  )
  const usernames = useMemo(() => liveAccounts.map((a) => a.username), [liveAccounts])
  const mfaBatch = useAgentMfaBatch(usernames, !demo && liveAccounts.length > 0)

  const mfaByUser = useMemo(() => {
    const map = new Map<string, AgentMfaStatus>()
    for (const u of mfaBatch.data?.users ?? []) {
      map.set(u.username.toLowerCase(), u)
      if (u.keycloakUsername) map.set(u.keycloakUsername.toLowerCase(), u)
    }
    return map
  }, [mfaBatch.data])

  const allRows = useMemo(() => {
    let list: AgentRow[] = liveAccounts.map((a, i) => {
      const live = mfaByUser.get(a.username.toLowerCase()) ?? null
      const demoMfa: AgentMfaStatus | null = demo
        ? {
            username: a.username,
            accountId: a.id,
            role: a.role,
            displayName: a.displayName,
            found: true,
            mfaEnabled: i % 3 !== 2,
            methods:
              i % 3 === 0
                ? ['totp', 'passkey']
                : i % 3 === 1
                  ? ['totp']
                  : [],
            keycloakUsername: null,
          }
        : null
      return {
        id: a.id,
        username: a.username,
        displayName: a.displayName || a.username,
        role: a.role,
        mfa: live?.found ? live : demoMfa,
      }
    })
    if (mfa === 'on') list = list.filter((r) => r.mfa?.mfaEnabled)
    if (mfa === 'off') list = list.filter((r) => r.mfa && !r.mfa.mfaEnabled)
    return list
  }, [liveAccounts, mfaByUser, demo, mfa])

  const total = allRows.length
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const safePage = Math.min(page, pageCount)
  const rows = useMemo(() => {
    const from = (safePage - 1) * PAGE_SIZE
    return allRows.slice(from, from + PAGE_SIZE)
  }, [allRows, safePage])

  const summary = useMemo(() => {
    const statuses = allRows.map((r) => r.mfa).filter(Boolean) as AgentMfaStatus[]
    return demoAgentSummary(
      statuses.length
        ? statuses
        : liveAccounts.map((a) => ({
            username: a.username,
            found: false,
            mfaEnabled: false,
            methods: [],
          })),
    )
  }, [allRows, liveAccounts])

  const asOf = downlineQuery.dataUpdatedAt || mfaBatch.dataUpdatedAt || undefined

  const columns = useMemo<Column<AgentRow>[]>(
    () => [
      {
        id: 'user',
        header: 'Agent',
        width: 200,
        cell: (row) => (
          <div style={{ minWidth: 0 }}>
            <div className="w7-ellipsis" style={{ fontWeight: 600 }}>
              <AccountLink id={row.id} username={row.username} />
            </div>
            <div className="w7-card-sub w7-ellipsis">{row.displayName}</div>
          </div>
        ),
        sortValue: (row) => row.username,
      },
      {
        id: 'role',
        header: 'Tier',
        width: 72,
        cell: (row) => <RoleChip role={row.role} label={ROLE_SHORT[row.role]} />,
        sortValue: (row) => ROLE_LABEL[row.role],
      },
      {
        id: 'status',
        header: '2FA',
        width: 88,
        cell: (row) => {
          if (!row.mfa || !row.mfa.found) {
            return (
              <Chip size="sm" tone="muted">
                —
              </Chip>
            )
          }
          return (
            <Chip size="sm" tone={row.mfa.mfaEnabled ? 'profit' : 'muted'}>
              {row.mfa.mfaEnabled ? 'On' : 'Off'}
            </Chip>
          )
        },
        sortValue: (row) => (row.mfa?.mfaEnabled ? 1 : 0),
      },
      {
        id: 'methods',
        header: 'Methods',
        width: 200,
        cell: (row) => (
          <div className="w7-row w7-row-wrap" style={{ gap: 4 }}>
            {!row.mfa?.methods.length ? (
              <span className="w7-card-sub">{EMPTY}</span>
            ) : (
              row.mfa.methods.map((m) => (
                <Chip
                  key={m}
                  size="sm"
                  tone={m === 'totp' ? 'brand' : m === 'passkey' ? 'info' : 'muted'}
                >
                  {m === 'totp' ? 'App' : m === 'passkey' ? 'Passkey' : 'Recovery'}
                </Chip>
              ))
            )}
          </div>
        ),
      },
    ],
    [],
  )

  const goMember = (id: string) => {
    navigate(`/member/${encodeURIComponent(id)}`)
  }

  return (
    <>
      {demo || (!import.meta.env.DEV && !demo) ? (
        <div className="w7-sign2fa-banner">
          <Banner
            tone="info"
            title={demo ? 'Sample agent MFA on your downline' : 'Agent MFA is local-dev only'}
          >
            {demo
              ? 'Rows are real agents from your tree — click any row to open their page.'
              : 'Production needs a server-side Keycloak admin BFF for downline MFA.'}
          </Banner>
        </div>
      ) : null}

      <div className="w7-sign2fa-kpis">
        <Kpi size="sm" label="Agents" value={count(summary.totalAgents)} meta="CM → Master" />
        <Kpi
          size="sm"
          label="2FA on"
          value={count(summary.mfaEnabled)}
          tone="profit"
          meta={`${percent(summary.adoptionRate)} · ${count(summary.mfaDisabled)} off`}
          accent
        />
        <Kpi size="sm" label="App" value={count(summary.totpEnabled)} tone="brand" />
        <Kpi size="sm" label="Passkey" value={count(summary.passkeyEnabled)} tone="info" />
      </div>

      <div className="w7-sign2fa-toolbar">
        <SearchInput
          label="Search agents"
          placeholder="Username or name"
          value={q}
          width={220}
          onChange={(next) => {
            setQ(next)
            setPage(1)
          }}
        />
        <div className="w7-row w7-row-wrap" style={{ gap: 4 }} role="group" aria-label="2FA status">
          {(
            [
              ['all', 'All'],
              ['on', 'On'],
              ['off', 'Off'],
            ] as const
          ).map(([id, label]) => (
            <FilterChip
              key={id}
              active={mfa === id}
              onClick={() => {
                setMfa(id)
                setPage(1)
              }}
            >
              {label}
            </FilterChip>
          ))}
        </div>
        <span style={{ marginLeft: 'auto' }}>
          <AsOf at={asOf} polls={downlineQuery.isFetching || mfaBatch.isFetching} />
        </span>
      </div>

      {downlineQuery.isError ? (
        <StateBlock icon="error" title="Could not load agents" tone="error">
          {(downlineQuery.error as Error)?.message || 'Hierarchy request failed.'}
        </StateBlock>
      ) : downlineQuery.isLoading && !downlineQuery.data ? (
        <StateBlock icon="refresh" title="Loading…">
          Fetching agents in your downline.
        </StateBlock>
      ) : (
        <Card
          flush
          title="Agents"
          subtitle={`${count(total)} matching this filter · click a row to open`}
          footer={
            <Pager
              page={safePage}
              pageCount={pageCount}
              onChange={setPage}
              total={total}
              pageSize={PAGE_SIZE}
            />
          }
        >
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            label={`${count(total)} agents with console MFA status`}
            density="compact"
            onRowClick={(row) => goMember(row.id)}
            loading={downlineQuery.isFetching && !downlineQuery.data}
            skeletonRows={8}
            empty={
              <StateBlock icon="search" title="No agents match">
                Clear search or widen the filters.
              </StateBlock>
            }
          />
        </Card>
      )}
    </>
  )
}

export function Player2faPage() {
  const [audience, setAudience] = useState<SignIn2faAudience>('members')

  return (
    <div className="w7-sign2fa">
      <PageHead
        title="Sign-in 2FA"
        subtitle={
          audience === 'members'
            ? 'Portal MFA for members · authenticator or SMS'
            : 'Console MFA for agents · authenticator or passkey'
        }
        actions={
          <div className="w7-sign2fa-seg">
            <Segmented
              label="Whose 2FA"
              value={audience}
              onChange={setAudience}
              items={[
                { id: 'members', label: 'Members' },
                { id: 'agents', label: 'Agents' },
              ]}
            />
          </div>
        }
      />

      {audience === 'members' ? <MembersPanel /> : <AgentsPanel />}
    </div>
  )
}
